import useSWR from "swr"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import { gradingPending, gradingStalled } from "./types"
import type { BatchDetail, EvaluationBatch, StartBatchParams } from "./types"

const BATCHES_URL = `${SMOOTH_LINE_API_URL}/base_api/smoother/screen/dunning_evaluation_batches`
/** 実行中は進捗をこの間隔で読み直す */
export const POLL_INTERVAL_MS = 3000
/** 採点が止まっているようなとき（gradingStalled）に読み直す間隔 */
export const STALE_POLL_INTERVAL_MS = 30 * 1000

/**
 * プロンプトの比較の実行一覧と、選んだ実行の進捗・結果。compareTo を渡すと、同じ対象の直前の実行の代わりに
 * その実行（比べる相手。ふだんは本番の版の最新の結果）と比べた結果を読む
 */
export function useEvaluationBatches(batchId: number | null, compareTo?: number | null) {
  const { getWithToken, postWithToken } = useSmoothApi()
  const list = useSWR<{ data: EvaluationBatch[] }>(
    BATCHES_URL,
    async (url: string) => (await getWithToken(url)).data,
    {
      revalidateOnFocus: false,
      // 実行中の実行があれば読み直す（本番の版で流している間に、別の実行を開いていても終わりに気づけるように）
      refreshInterval: (data) =>
        data?.data.some((batch) => batch.status === "running") ? POLL_INTERVAL_MS : 0,
    }
  )
  const detail = useSWR<BatchDetail>(
    batchId ? `${BATCHES_URL}/${batchId}${compareTo ? `?compareTo=${compareTo}` : ""}` : null,
    async (url: string) => (await getWithToken(url)).data,
    {
      revalidateOnFocus: false,
      // 生成中と、生成を終えた後の採点中は進捗を読み直す。終えてから長く経っても採点が進まない（ジョブが止まった等）ときは間隔を空ける
      refreshInterval: (data) => {
        if (data?.data.status === "running") return POLL_INTERVAL_MS
        if (!gradingPending(data)) return 0
        return gradingStalled(data) ? STALE_POLL_INTERVAL_MS : POLL_INTERVAL_MS
      },
    }
  )

  /** 生成結果を「そのまま送れるか」で採点し始める（model 省略時はサーバの既定モデル） */
  const grade = async (id: number, model?: string): Promise<EvaluationBatch> => {
    const response = await postWithToken(`${BATCHES_URL}/${id}/grade`, model ? { model } : {})
    return response.data.data
  }

  return {
    list,
    detail,
    /** gradingModel を指定すると、実行を始めたあとで採点も始める（採点は生成を終えたものから順に進む） */
    async start({ gradingModel, ...params }: StartBatchParams): Promise<EvaluationBatch> {
      const response = await postWithToken(BATCHES_URL, params)
      const created: EvaluationBatch = response.data.data
      try {
        return gradingModel !== undefined
          ? await grade(created.id, gradingModel || undefined)
          : created
      } finally {
        // 採点を始められなくても、始めた実行は一覧に出す（実行中のまま見えなくならない）
        await list.mutate()
      }
    },
    async grade(id: number, model?: string): Promise<void> {
      await grade(id, model)
      await Promise.all([list.mutate(), detail.mutate()])
    },
    async cancel(id: number): Promise<void> {
      await postWithToken(`${BATCHES_URL}/${id}/cancel`, {})
      await Promise.all([list.mutate(), detail.mutate()])
    },
  }
}

export type EvaluationBatchesApi = ReturnType<typeof useEvaluationBatches>
